/**
 * Donor Service
 * Handles API calls for donor management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';
import { PaginatedResponse } from '../types/api';

export interface Donor {
  donorID: number;
  fullName: string;
  salutation: string;
  organizationCompany: string;
  emailAddress: string;
  phoneNumber: string;
  donorType: string;
  priorityLevel: string;
  interests: string[];
  createdAt: string;
}

export interface DonorFilters {
  searchQuery?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Get donors with optional search and pagination
 */
export async function getAllDonors(filters?: DonorFilters): Promise<PaginatedResponse<Donor>> {
  const response = await apiGet<Donor[]>('/donors', filters);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load donors. Please try again.');
  }

  return response as PaginatedResponse<Donor>;
}

/**
 * Get donor by ID
 */
export async function getDonorById(donorID: number): Promise<Donor> {
  const response = await apiGet<Donor>(`/donors/${donorID}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load donor details. Please try again.');
  }

  return response.data;
}

/**
 * Create new donor
 */
export async function createDonor(donor: Partial<Donor>): Promise<Donor> {
  const response = await apiPost<Donor>('/donors', donor);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create donor. Please try again.');
  }

  return response.data;
}

/**
 * Update donor
 */
export async function updateDonor(donorID: number, donor: Partial<Donor>): Promise<Donor> {
  const response = await apiPut<Donor>(`/donors/${donorID}`, donor);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update donor. Please try again.');
  }

  return response.data;
}

/**
 * Delete donor
 */
export async function deleteDonor(donorID: number): Promise<void> {
  const response = await apiDelete<void>(`/donors/${donorID}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete donor. Please try again.');
  }
}
